import { useState, useEffect } from 'react';
import {
    Plus,
    Edit01,
    Trash01,
    SearchLg,
    ArrowRight
} from '@untitledui/icons';
import { Button } from '@/components/base/buttons/button';
import { Input } from '@/components/base/input/input';
import { Select } from '@/components/base/select/select';
import { Badge } from '@/components/base/badges/badges';
import { Table, TableCard } from '@/components/application/table/table';
import { Modal, DialogTrigger, Dialog, ModalOverlay } from '@/components/application/modals/modal';
import { documentSchemaService, documentAttributeService, schemaAttributeService, connectionService, DOCUMENT_TYPES } from '@/data/mock-document-schemas';
import type { DocumentTypeConnection, CreateDocumentTypeConnectionData, DocumentType, DocumentSchema, DocumentAttribute } from '@/types/document-schema';
import { useTranslation } from 'react-i18next';

export const DocConnectionManagerPage = () => {
    const { t } = useTranslation(['pages:documents-admin', 'common']);
    const [connections, setConnections] = useState<DocumentTypeConnection[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingConnection, setEditingConnection] = useState<DocumentTypeConnection | null>(null);

    const [formData, setFormData] = useState<CreateDocumentTypeConnectionData>({
        source_document_type: 'bill_of_lading',
        source_schema_id: '',
        source_attribute_id: '',
        target_document_type: 'commercial_invoice',
        target_schema_id: '',
        target_attribute_id: '',
        validation_rule: 'must_match',
    });

    const [sourceSchemas, setSourceSchemas] = useState<DocumentSchema[]>([]);
    const [sourceAttributes, setSourceAttributes] = useState<DocumentAttribute[]>([]);
    const [targetSchemas, setTargetSchemas] = useState<DocumentSchema[]>([]);
    const [targetAttributes, setTargetAttributes] = useState<DocumentAttribute[]>([]);

    const documentTypeOptions = DOCUMENT_TYPES.map((dt) => ({ id: dt.key, label: dt.label }));

    const validationRuleOptions = [
        { id: 'must_match', label: t('connections.validationRules.mustMatch') },
        { id: 'must_be_different', label: t('connections.validationRules.mustBeDifferent') },
        { id: 'source_contains_target', label: t('connections.validationRules.sourceContainsTarget') },
        { id: 'target_contains_source', label: t('connections.validationRules.targetContainsSource') },
        { id: 'must_reference', label: t('connections.validationRules.mustReference') },
        { id: 'must_be_similar', label: t('connections.validationRules.mustBeSimilar') },
    ];

    useEffect(() => {
        loadConnections();
    }, [searchTerm]);

    useEffect(() => {
        if (formData.source_document_type) {
            const schemas = documentSchemaService.getAll().filter(
                (s) => s.document_type === formData.source_document_type && !s.deleted_at
            );
            setSourceSchemas(schemas);
            if (formData.source_schema_id) {
                loadSourceAttributes(formData.source_schema_id);
            } else {
                setSourceAttributes([]);
            }
        }
    }, [formData.source_document_type, formData.source_schema_id]);

    useEffect(() => {
        if (formData.target_document_type) {
            const schemas = documentSchemaService.getAll().filter(
                (s) => s.document_type === formData.target_document_type && !s.deleted_at
            );
            setTargetSchemas(schemas);
            if (formData.target_schema_id) {
                loadTargetAttributes(formData.target_schema_id);
            } else {
                setTargetAttributes([]);
            }
        }
    }, [formData.target_document_type, formData.target_schema_id]);

    const loadConnections = () => {
        setIsLoading(true);
        const allConnections = connectionService.getAll();

        let filtered = allConnections;
        if (searchTerm) {
            const term = searchTerm.toLowerCase();
            filtered = filtered.filter((conn) => {
                const sourceDocType = DOCUMENT_TYPES.find((dt) => dt.key === conn.source_document_type)?.label || '';
                const targetDocType = DOCUMENT_TYPES.find((dt) => dt.key === conn.target_document_type)?.label || '';
                return sourceDocType.toLowerCase().includes(term) || targetDocType.toLowerCase().includes(term);
            });
        }

        setConnections(filtered);
        setIsLoading(false);
    };

    const loadSourceAttributes = (schemaId: string) => {
        const schemaAttrs = schemaAttributeService.getBySchemaId(schemaId);
        const attrs = schemaAttrs.map((sa) => sa.attribute).filter(Boolean) as DocumentAttribute[];
        setSourceAttributes(attrs);
    };

    const loadTargetAttributes = (schemaId: string) => {
        const schemaAttrs = schemaAttributeService.getBySchemaId(schemaId);
        const attrs = schemaAttrs.map((sa) => sa.attribute).filter(Boolean) as DocumentAttribute[];
        setTargetAttributes(attrs);
    };

    const handleOpenModal = (connection?: DocumentTypeConnection) => {
        if (connection) {
            setEditingConnection(connection);
            setFormData({
                source_document_type: connection.source_document_type,
                source_schema_id: connection.source_schema_id,
                source_attribute_id: connection.source_attribute_id,
                target_document_type: connection.target_document_type,
                target_schema_id: connection.target_schema_id,
                target_attribute_id: connection.target_attribute_id,
                validation_rule: connection.validation_rule,
            });
        } else {
            setEditingConnection(null);
            setFormData({
                source_document_type: 'bill_of_lading',
                source_schema_id: '',
                source_attribute_id: '',
                target_document_type: 'commercial_invoice',
                target_schema_id: '',
                target_attribute_id: '',
                validation_rule: 'must_match',
            });
        }
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setEditingConnection(null);
    };

    const handleSave = () => {
        if (editingConnection) {
            connectionService.update(editingConnection.connection_id, {
                ...formData,
                connection_id: editingConnection.connection_id,
            });
        } else {
            connectionService.create(formData);
        }
        loadConnections();
        handleCloseModal();
    };

    const handleDelete = (id: string) => {
        if (confirm(t('modal.confirmDelete'))) {
            connectionService.delete(id);
            loadConnections();
        }
    };

    const getSourceDisplay = (conn: DocumentTypeConnection) => {
        const docType = DOCUMENT_TYPES.find((dt) => dt.key === conn.source_document_type);
        const schema = documentSchemaService.getById(conn.source_schema_id);
        const attribute = documentAttributeService.getById(conn.source_attribute_id);
        return `${docType?.label || conn.source_document_type} > ${schema?.schema_name || conn.source_schema_id} > ${attribute?.label || conn.source_attribute_id}`;
    };

    const getTargetDisplay = (conn: DocumentTypeConnection) => {
        const docType = DOCUMENT_TYPES.find((dt) => dt.key === conn.target_document_type);
        const schema = documentSchemaService.getById(conn.target_schema_id);
        const attribute = documentAttributeService.getById(conn.target_attribute_id);
        return `${docType?.label || conn.target_document_type} > ${schema?.schema_name || conn.target_schema_id} > ${attribute?.label || conn.target_attribute_id}`;
    };

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand mx-auto mb-4"></div>
                    <p className="text-tertiary">{t('loading')}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="text-xl font-semibold text-primary">{t('connections.title')}</h2>
                    <p className="text-tertiary mt-1">{t('connections.description')}</p>
                </div>
                <Button
                    onClick={() => handleOpenModal()}
                    size="md"
                    color="primary"
                    iconLeading={Plus}
                >
                    {t('connections.newConnection')}
                </Button>
            </div>

            {/* Search */}
            <div className="rounded-xl bg-primary p-6 shadow-xs ring-1 ring-secondary">
                <Input
                    placeholder={t('connections.searchPlaceholder')}
                    value={searchTerm}
                    onChange={setSearchTerm}
                    icon={SearchLg}
                    size="md"
                />
            </div>

            {/* Connections Table */}
            <TableCard.Root>
                <TableCard.Header
                    title={`${t('connections.title')} (${connections.length})`}
                    description={t('connections.description')}
                />

                <Table>
                    <Table.Header>
                        <Table.Head label={t('connections.headers.source')} isRowHeader />
                        <Table.Head label={t('connections.headers.target')} />
                        <Table.Head label={t('connections.headers.validationRule')} />
                        <Table.Head label={t('connections.headers.created')} />
                        <Table.Head label={t('connections.headers.actions')} />
                    </Table.Header>
                    <Table.Body>
                        {connections.map((conn) => (
                            <Table.Row key={conn.connection_id}>
                                <Table.Cell>
                                    <div className="font-medium text-primary">{getSourceDisplay(conn)}</div>
                                </Table.Cell>
                                <Table.Cell>
                                    <div className="font-medium text-primary flex items-center gap-2">
                                        <ArrowRight className="size-4 text-tertiary" />
                                        {getTargetDisplay(conn)}
                                    </div>
                                </Table.Cell>
                                <Table.Cell>
                                    <Badge color="blue" size="sm">
                                        {validationRuleOptions.find((r) => r.id === conn.validation_rule)?.label || conn.validation_rule}
                                    </Badge>
                                </Table.Cell>
                                <Table.Cell>
                                    <span className="text-sm text-tertiary">
                                        {conn.created_at.toLocaleDateString('it-IT')}
                                    </span>
                                </Table.Cell>
                                <Table.Cell>
                                    <div className="flex items-center gap-2">
                                        <Button
                                            onClick={() => handleOpenModal(conn)}
                                            size="sm"
                                            color="secondary"
                                            iconLeading={Edit01}
                                        >
                                            {t('connections.actions.edit')}
                                        </Button>
                                        <Button
                                            onClick={() => handleDelete(conn.connection_id)}
                                            size="sm"
                                            color="secondary-destructive"
                                            iconLeading={Trash01}
                                        >
                                            {t('connections.actions.delete')}
                                        </Button>
                                    </div>
                                </Table.Cell>
                            </Table.Row>
                        ))}
                    </Table.Body>
                </Table>
            </TableCard.Root>

            {connections.length === 0 && (
                <div className="text-center py-12">
                    <div className="text-tertiary mb-4">
                        <h3 className="text-lg font-medium text-primary mb-2">{t('noData.title')}</h3>
                        <p className="text-tertiary">{t('noData.message')}</p>
                    </div>
                </div>
            )}

            {/* Modal */}
            <DialogTrigger isOpen={isModalOpen} onOpenChange={setIsModalOpen}>
                <ModalOverlay>
                    <Modal>
                        <Dialog>
                            <div className="w-full max-w-4xl rounded-xl bg-primary p-6 shadow-lg ring-1 ring-secondary max-h-[90vh] overflow-y-auto">
                                <h2 className="text-xl font-semibold text-primary mb-6">
                                    {editingConnection ? t('connections.modal.edit') : t('connections.modal.new')}
                                </h2>

                                <div className="space-y-4">
                                    {/* Source Section */}
                                    <div className="space-y-4 p-4 rounded-lg bg-secondary">
                                        <h3 className="text-lg font-semibold text-primary">{t('connections.modal.source')}</h3>

                                        <Select
                                            label={t('connections.modal.fields.sourceDocumentType')}
                                            items={documentTypeOptions}
                                            selectedKey={formData.source_document_type}
                                            onSelectionChange={(key) => {
                                                setFormData({
                                                    ...formData,
                                                    source_document_type: key as DocumentType,
                                                    source_schema_id: '',
                                                    source_attribute_id: '',
                                                });
                                            }}
                                            isRequired
                                            size="md"
                                        >
                                            {(item) => (
                                                <Select.Item key={item.id} id={item.id}>
                                                    {item.label}
                                                </Select.Item>
                                            )}
                                        </Select>

                                        <Select
                                            label={t('connections.modal.fields.sourceSchema')}
                                            items={sourceSchemas.map((s) => ({ id: s.document_schema_id, label: s.schema_name }))}
                                            selectedKey={formData.source_schema_id}
                                            onSelectionChange={(key) => {
                                                setFormData({
                                                    ...formData,
                                                    source_schema_id: key as string,
                                                    source_attribute_id: '',
                                                });
                                            }}
                                            isRequired
                                            size="md"
                                        >
                                            {(item) => (
                                                <Select.Item key={item.id} id={item.id}>
                                                    {item.label}
                                                </Select.Item>
                                            )}
                                        </Select>

                                        <Select
                                            label={t('connections.modal.fields.sourceAttribute')}
                                            items={sourceAttributes.map((a) => ({ id: a.document_attribute_id, label: `${a.label} (${a.attribute_key})` }))}
                                            selectedKey={formData.source_attribute_id}
                                            onSelectionChange={(key) => setFormData({ ...formData, source_attribute_id: key as string })}
                                            isRequired
                                            size="md"
                                        >
                                            {(item) => (
                                                <Select.Item key={item.id} id={item.id}>
                                                    {item.label}
                                                </Select.Item>
                                            )}
                                        </Select>
                                    </div>

                                    {/* Target Section */}
                                    <div className="space-y-4 p-4 rounded-lg bg-secondary">
                                        <h3 className="text-lg font-semibold text-primary">{t('connections.modal.target')}</h3>

                                        <Select
                                            label={t('connections.modal.fields.targetDocumentType')}
                                            items={documentTypeOptions}
                                            selectedKey={formData.target_document_type}
                                            onSelectionChange={(key) => {
                                                setFormData({
                                                    ...formData,
                                                    target_document_type: key as DocumentType,
                                                    target_schema_id: '',
                                                    target_attribute_id: '',
                                                });
                                            }}
                                            isRequired
                                            size="md"
                                        >
                                            {(item) => (
                                                <Select.Item key={item.id} id={item.id}>
                                                    {item.label}
                                                </Select.Item>
                                            )}
                                        </Select>

                                        <Select
                                            label={t('connections.modal.fields.targetSchema')}
                                            items={targetSchemas.map((s) => ({ id: s.document_schema_id, label: s.schema_name }))}
                                            selectedKey={formData.target_schema_id}
                                            onSelectionChange={(key) => {
                                                setFormData({
                                                    ...formData,
                                                    target_schema_id: key as string,
                                                    target_attribute_id: '',
                                                });
                                            }}
                                            isRequired
                                            size="md"
                                        >
                                            {(item) => (
                                                <Select.Item key={item.id} id={item.id}>
                                                    {item.label}
                                                </Select.Item>
                                            )}
                                        </Select>

                                        <Select
                                            label={t('connections.modal.fields.targetAttribute')}
                                            items={targetAttributes.map((a) => ({ id: a.document_attribute_id, label: `${a.label} (${a.attribute_key})` }))}
                                            selectedKey={formData.target_attribute_id}
                                            onSelectionChange={(key) => setFormData({ ...formData, target_attribute_id: key as string })}
                                            isRequired
                                            size="md"
                                        >
                                            {(item) => (
                                                <Select.Item key={item.id} id={item.id}>
                                                    {item.label}
                                                </Select.Item>
                                            )}
                                        </Select>
                                    </div>

                                    {/* Validation Rule */}
                                    <Select
                                        label={t('connections.modal.fields.validationRule')}
                                        items={validationRuleOptions}
                                        selectedKey={formData.validation_rule || 'must_match'}
                                        onSelectionChange={(key) => setFormData({ ...formData, validation_rule: key as string })}
                                        size="md"
                                    >
                                        {(item) => (
                                            <Select.Item key={item.id} id={item.id}>
                                                {item.label}
                                            </Select.Item>
                                        )}
                                    </Select>
                                </div>

                                <div className="flex items-center justify-end gap-4 mt-6">
                                    <Button onClick={handleCloseModal} size="md" color="secondary">
                                        {t('modal.cancel')}
                                    </Button>
                                    <Button onClick={handleSave} size="md" color="primary">
                                        {editingConnection ? t('modal.save') : t('modal.create')}
                                    </Button>
                                </div>
                            </div>
                        </Dialog>
                    </Modal>
                </ModalOverlay>
            </DialogTrigger>
        </div>
    );
};
